import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDiscoveryStore } from '../stores/useDiscoveryStore';

export function useDiscoverySessionUrl() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSessionId = searchParams.get('sessionId') ?? null;
  const pendingSessionId = useDiscoveryStore((s) => s.pendingSessionId);
  const setPendingSessionId = useDiscoveryStore((s) => s.setPendingSessionId);

  useEffect(() => {
    if (urlSessionId && pendingSessionId && urlSessionId === pendingSessionId) {
      setPendingSessionId(null);
    }
  }, [urlSessionId, pendingSessionId, setPendingSessionId]);

  const sessionId = urlSessionId || pendingSessionId;

  const setSessionId = useCallback(
    (
      id: string | null,
      options?: { preserveArtifactId?: boolean }
    ) => {
      const preserveArtifactId = options?.preserveArtifactId === true;

      // Switching (or clearing) the session invalidates any in-flight
      // artifact deep-link belonging to the previous session — strip
      // `artifactId` from the URL, reset the store guards so the
      // deep-link effect does not misfire against the new session, and
      // close the insights/artifact side panel so the user sees the
      // new session cleanly instead of a stale artifact view.
      //
      // Exception: the deep-link resolver itself calls `setSessionId`
      // with `preserveArtifactId: true` when it auto-switches to the
      // session that owns the requested artifact, so the pending
      // deep-link remains active after the switch.
      if (!preserveArtifactId) {
        const store = useDiscoveryStore.getState();
        store.setLastResolvedDeepLinkArtifactId(null);
        store.setActiveArtifactId(null);
        store.setActiveArtifactRecordId(null);
        if (store.isInsightsPanelOpen) {
          store.closeInsights();
        }
      }

      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id) {
            next.set('sessionId', id);
          } else {
            next.delete('sessionId');
          }
          if (!preserveArtifactId) {
            next.delete('artifactId');
            next.delete('selectedArtifact');
          }
          return next;
        },
        { replace: false }
      );
    },
    [setSearchParams]
  );

  return { sessionId, setSessionId };
}
